import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import useReveal from '../../hooks/useReveal';
import useChartReveal from '../../hooks/useChartReveal';
import { BAR_ANIMATION_MS, REVEAL_DELAY_MS, REVEAL_DURATION_MS, toMs } from '../../constants/animation';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

interface AudienceRendererProps {
  title: string;
  data: any;
  primaryColor?: string;
}

export default function AudienceRenderer({ title, data, primaryColor = '#14B8A6' }: AudienceRendererProps) {
  if (!data || typeof data !== 'object') return null;
  const genderReveal = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  const ageReveal = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  const incomeReveal = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  const maritalReveal = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  const childrenReveal = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  const educationReveal = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  const employmentReveal = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });

  const ageChartRef = useChartReveal(ageReveal.isVisible);
  const incomeChartRef = useChartReveal(incomeReveal.isVisible);
  const maritalChartRef = useChartReveal(maritalReveal.isVisible);
  const childrenChartRef = useChartReveal(childrenReveal.isVisible);
  const educationChartRef = useChartReveal(educationReveal.isVisible);

  const cardStyle = (visible: boolean, offset = 0) => {
    const delay = visible ? REVEAL_DELAY_MS + offset : 0;
    return {
      transition: `transform ${toMs(REVEAL_DURATION_MS)} ease-out, opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      opacity: visible ? 1 : 0.25,
      transitionDelay: toMs(delay),
    };
  };

  const chartFadeStyle = (visible: boolean, offset = 0) => {
    const delay = visible ? REVEAL_DELAY_MS + offset : 0;
    return {
      transition: `opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
      opacity: visible ? 1 : 0,
      transitionDelay: toMs(delay),
    };
  };

  // Extract gender and age data from the audience data structure
  const genderData = data.gender || data.Gender || {};
  const ageData = data.age || data.ageGroup || data['Age Group'] || {};

  const renderGenderChart = () => {
    const m = typeof (genderData?.Male ?? genderData?.male) === 'number' ? (genderData.Male ?? genderData.male) : undefined;
    const f = typeof (genderData?.Female ?? genderData?.female) === 'number' ? (genderData.Female ?? genderData.female) : undefined;
    if (m == null && f == null) return null;
    let male = typeof m === 'number' ? m : undefined;
    let female = typeof f === 'number' ? f : undefined;
    if (male != null && female == null) female = Math.max(0, 100 - male);
    if (female != null && male == null) male = Math.max(0, 100 - female);
    if (male == null || female == null) return null; // still incomplete

    const labels = ['Male', 'Female'];
    const values = [male, female];

    const displayValues = genderReveal.isVisible ? values : values.map(() => 0.0001);

    const doughnutData = {
      labels,
      datasets: [{
        data: displayValues,
        backgroundColor: [primaryColor, '#FDBA19'],
        borderWidth: 0
      }]
    };

    const doughnutOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right' as const,
          labels: {
            usePointStyle: true,
            pointStyle: 'rect',
            padding: 20,
            font: { size: 12 }
          }
        }
      },
      cutout: '60%',
    };

    return (
      <div
        ref={genderReveal.ref}
        className="bg-white rounded-lg p-4"
        style={cardStyle(genderReveal.isVisible)}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Gender</h3>
        <div className="text-xs text-gray-500 mb-4">% COMPOSITION</div>
        <div className="text-xs text-gray-500 mb-4">Online Adult Population</div>
        <div className="h-64">
          <Doughnut data={doughnutData} options={{
            ...doughnutOptions,
            animation: {
              duration: genderReveal.isVisible ? BAR_ANIMATION_MS : 0,
              easing: 'easeOutCubic',
              delay: genderReveal.isVisible ? REVEAL_DELAY_MS : 0,
            },
          }} />
        </div>
      </div>
    );
  };

  const renderAgeChart = () => {
    // Support an optional first bucket for "Under 18" in addition to the standard buckets
    const CANON = ['Under 18','18-24','25-34','35-44','45-49','50-54','55-64','65+'];
    if (!ageData || typeof ageData !== 'object') return null;
    const normalize = (s: string) => s.toLowerCase().replace(/\s+/g,'').replace(/–|—/g,'-');
    const getVal = (want: string, aliases: string[]): number | null => {
      const keys = Object.keys(ageData as any);
      const wantSet = new Set([normalize(want), ...aliases.map(a=>normalize(a))]);
      for (const k of keys) {
        const nk = normalize(k);
        if (wantSet.has(nk)) {
          const v = (ageData as any)[k];
          if (typeof v === 'number') return v;
          if (typeof v === 'string') {
            const n = Number(String(v).replace(/[^0-9.\-]/g,''));
            if (Number.isFinite(n)) return n;
          }
        }
      }
      return null;
    };
    const labelAliases: Record<string, string[]> = {
      'Under 18': ['under18','<18','0-17','0to17','18andunder','under age 18','< 18'],
      '18-24': ['18to24','18–24','18 — 24','18 —24','18 – 24'],
      '25-34': ['25to34','25–34','25 – 34'],
      '35-44': ['35to44','35–44','35 – 44'],
      '45-49': ['45to49','45–49','45 – 49'],
      '50-54': ['50to54','50–54','50 – 54'],
      '55-64': ['55to64','55–64','55 – 64'],
      '65+': ['65plus','65andover','65andover','65 and older','65 and over']
    };
    const labels = CANON;
    const values = CANON.map(k => getVal(k, labelAliases[k] || [])).map(v => (typeof v === 'number' ? v : 0));
    const hasAny = values.some(v => v > 0);
    if (!hasAny) return null;

    const barData = {
      labels,
      datasets: [{
        data: values,
        backgroundColor: primaryColor,
        borderRadius: { topLeft: 30, topRight: 30 } as any,
        barThickness: 40
      }]
    };

    const barOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false }
      },
      scales: {
        x: {
          ticks: { 
            color: '#6B7280',
            font: { size: 10 }
          },
          grid: { display: false }
        },
        y: {
          ticks: { 
            color: '#6B7280',
            font: { size: 10 }
          },
          beginAtZero: true,
          max: 30,
          grid: { color: '#E5E7EB' }
        }
      }
    };

    return (
      <div
        ref={ageReveal.ref}
        className="bg-white rounded-lg p-4"
        style={cardStyle(ageReveal.isVisible)}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Age Group</h3>
        <div className="text-xs text-gray-500 mb-4">% COMPOSITION</div>
        <div className="text-xs text-gray-500 mb-4">Online Adult Population</div>
        <div className="h-64" style={chartFadeStyle(ageReveal.isVisible)}>
          <Bar
            ref={ageChartRef}
            data={barData}
            options={{
              ...barOptions,
              animation: {
                duration: ageReveal.isVisible ? BAR_ANIMATION_MS : 0,
                easing: 'easeOutCubic',
                delay: ageReveal.isVisible ? REVEAL_DELAY_MS : 0,
              },
            }}
          />
        </div>
      </div>
    );
  };

  // Household Income (% composition) vertical bars
  const renderIncomeChart = () => {
    const income = (data.income || data.Income || data['Household Income'] || {}) as Record<string, any>;
    const BUCKETS = ['<$25k', '$25k-50k', '$50k-75k', '$75k-100k', '$100k-150k', '$150k-200k', '$200k-250k', '$250k+'];
    if (!income || typeof income !== 'object') return null;
    const labels = BUCKETS;
    const values = BUCKETS.map(k => (typeof income[k] === 'number' ? income[k] : 0));
    const hasAny = values.some(v => v > 0);
    if (!hasAny) return null;
    const barData = { labels, datasets: [{ data: values, backgroundColor: primaryColor, borderRadius: 4, barThickness: 32 }] };
    const barOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280', font: { size: 9 } }, grid: { display: false } }, y: { ticks: { color: '#6B7280', font: { size: 10 } }, beginAtZero: true, grid: { color: '#E5E7EB' } } } } as const;
    return (
      <div
        ref={incomeReveal.ref}
        className="bg-white rounded-lg p-4"
        style={cardStyle(incomeReveal.isVisible)}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Household Income</h3>
        <div className="text-xs text-gray-500 mb-4">% COMPOSITION</div>
        <div className="text-xs text-gray-500 mb-4">Online Adult Population</div>
        <div className="h-64" style={chartFadeStyle(incomeReveal.isVisible)}>
          <Bar
            ref={incomeChartRef}
            data={barData}
            options={{
              ...barOptions,
              animation: {
                duration: incomeReveal.isVisible ? BAR_ANIMATION_MS : 0,
                easing: 'easeOutCubic',
                delay: incomeReveal.isVisible ? REVEAL_DELAY_MS : 0,
              },
            }}
          />
        </div>
      </div>
    );
  };

  // Marital Status (use horizontal bars for readability)
  const renderMaritalChart = () => {
    const marital = (data.marital || data['Marital Status'] || {}) as Record<string, any>;
    if (!marital || typeof marital !== 'object') return null;
    const LABELS = ['Single', 'Married', 'Divorced', 'Widow/Widower'];
    const labels = LABELS;
    const values = LABELS.map(k => (typeof marital[k] === 'number' ? marital[k] : 0));
    const hasAny = values.some(v => v > 0);
    if (!hasAny) return null;
    const barData = { labels, datasets: [{ data: values, backgroundColor: primaryColor, borderRadius: 999 }] };
    const barOptions: any = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, indexAxis: 'y', scales: { x: { ticks: { color: '#6B7280', font: { size: 10 } }, beginAtZero: true, grid: { color: '#E5E7EB' } }, y: { ticks: { color: '#6B7280', font: { size: 10 } }, grid: { display: false } } } };
    return (
      <div
        ref={maritalReveal.ref}
        className="bg-white rounded-lg p-4"
        style={cardStyle(maritalReveal.isVisible)}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Marital Status</h3>
        <div className="text-xs text-gray-500 mb-4">% COMPOSITION</div>
        <div className="text-xs text-gray-500 mb-4">Online Adult Population</div>
        <div className="h-64" style={chartFadeStyle(maritalReveal.isVisible)}>
          <Bar
            ref={maritalChartRef}
            data={barData}
            options={{
              ...barOptions,
              animation: {
                duration: maritalReveal.isVisible ? BAR_ANIMATION_MS : 0,
                easing: 'easeOutCubic',
                delay: maritalReveal.isVisible ? REVEAL_DELAY_MS : 0,
              },
            }}
          />
        </div>
      </div>
    );
  };

  // Number of Children Under 18 Years (vertical bars)
  const renderChildrenChart = () => {
    const children = (data.children || data['Number of Children Under 18 Years'] || {}) as Record<string, any>;
    if (!children || typeof children !== 'object') return null;
    const LABELS = ['No Children', '1 Child', '2 or 3 Children', '4 or 5 Children', '6+ Children'];
    const labels = LABELS;
    const values = LABELS.map(k => (typeof children[k] === 'number' ? children[k] : 0));
    const hasAny = values.some(v => v > 0);
    if (!hasAny) return null;
    const barData = { labels, datasets: [{ data: values, backgroundColor: primaryColor, borderRadius: 4, barThickness: 32 }] };
    const barOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280', font: { size: 10 } }, grid: { display: false } }, y: { ticks: { color: '#6B7280', font: { size: 10 } }, beginAtZero: true, grid: { color: '#E5E7EB' } } } } as const;
    return (
      <div
        ref={childrenReveal.ref}
        className="bg-white rounded-lg p-4"
        style={cardStyle(childrenReveal.isVisible)}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Number of Children Under 18 Years</h3>
        <div className="text-xs text-gray-500 mb-4">% COMPOSITION</div>
        <div className="text-xs text-gray-500 mb-4">Online Adult Population</div>
        <div className="h-64" style={chartFadeStyle(childrenReveal.isVisible)}>
          <Bar
            ref={childrenChartRef}
            data={barData}
            options={{
              ...barOptions,
              animation: {
                duration: childrenReveal.isVisible ? BAR_ANIMATION_MS : 0,
                easing: 'easeOutCubic',
                delay: childrenReveal.isVisible ? REVEAL_DELAY_MS : 0,
              },
            }}
          />
        </div>
      </div>
    );
  };
  // Specific Education card within Audience
  const renderEducationCard = () => {
    const edu = data.education || data.Education;
    if (!edu || typeof edu !== 'object') return null;
    const BUCKETS = ['Less Than High School','High School Degree','Some College','College Degree','Post-grad Degree'];
    const labels = BUCKETS;
    const values = BUCKETS.map(k => (typeof (edu as any)[k] === 'number' ? (edu as any)[k] : 0));
    const hasAny = values.some(v => v > 0);
    if (!hasAny) return null;
    const barData = { labels, datasets: [{ data: values, backgroundColor: primaryColor, borderRadius: { topLeft: 30, topRight: 30 } as any, barThickness: 40 }] };
    const barOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280', font: { size: 10 } }, grid: { display: false } }, y: { ticks: { color: '#6B7280', font: { size: 10 } }, beginAtZero: true, grid: { color: '#E5E7EB' } } } } as const;
    return (
      <div
        ref={educationReveal.ref}
        className="bg-white rounded-lg p-4"
        style={cardStyle(educationReveal.isVisible)}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Education</h3>
        <div className="text-xs text-gray-500 mb-4">% COMPOSITION</div>
        <div className="text-xs text-gray-500 mb-4">Online Adult Population</div>
        <div className="h-64" style={chartFadeStyle(educationReveal.isVisible)}>
          <Bar
            ref={educationChartRef}
            data={barData}
            options={{
              ...barOptions,
              animation: {
                duration: educationReveal.isVisible ? BAR_ANIMATION_MS : 0,
                easing: 'easeOutCubic',
                delay: educationReveal.isVisible ? REVEAL_DELAY_MS : 0,
              },
            }}
          />
        </div>
      </div>
    );
  };

  // Specific Employment Status card within Audience (single meter for FULL-TIME)
  const renderEmploymentCard = () => {
    const emp = data.employment || data['Employment Status'] || data.Employment || null;
    if (!emp || typeof emp !== 'object') return null;
    const fullKey = Object.keys(emp).find(k => k.toLowerCase().includes('full'));
    if (!fullKey) return null;
    const entry: any = (emp as any)[fullKey];
    const indexVal = typeof entry?.index === 'number' ? entry.index : (typeof entry === 'number' ? entry : 0);
    const compositionVal = typeof entry?.composition === 'number' ? entry.composition : undefined;
    if (indexVal <= 0) return null;

    const pct = Math.max(0, Math.min(100, compositionVal ?? 0));
    const widthPct = Math.max(0, Math.min(100, indexVal));
    return (
      <div
        ref={employmentReveal.ref}
        className="bg-white rounded-lg p-4"
        style={cardStyle(employmentReveal.isVisible)}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Employment Status</h3>
        <div className="text-xs text-gray-500 mb-4">BY INDEX</div>
        <div className="font-extrabold tracking-wide mb-2" style={{ color: primaryColor }}>FULL-TIME</div>
        <div className="flex items-center gap-6 mb-4">
          <div className="text-gray-900 rounded-md px-3 py-2 text-center" style={{ backgroundColor: `${primaryColor}20` }}>
            <div className="text-xs text-gray-600">INDEX</div>
            <div className="text-2xl font-bold">{indexVal}</div>
          </div>
          {compositionVal != null && (
            <div className="text-right">
              <div className="text-xs text-gray-600">% COMPOSITION</div>
              <div className="text-2xl font-extrabold" style={{ color: primaryColor }}>{pct}%</div>
            </div>
          )}
        </div>
        <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full flex items-center justify-end pr-2 text-white text-xs font-bold"
            style={{
              width: `${employmentReveal.isVisible ? widthPct : 0}%`,
              backgroundColor: primaryColor,
              transition: `width ${toMs(BAR_ANIMATION_MS)} ease-out`,
              transitionDelay: employmentReveal.isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
            }}
          >
            {indexVal}
          </div>
        </div>
        <div className="text-center mt-2 text-xs text-gray-400">100<br/>Online Adult Population</div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header in its own white box */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <p className="text-gray-600">BASELINE: Online Adult Population | CONTAINS: Engaged Audiences Data</p>
          </div>
        {/* Metrics panel: show Projected Adult Population when provided */}
        {(data as any)?.metrics?.projectedAdultPopulation ? (
          <div className="text-right">
            <div className="text-sm text-gray-600">PROJECTED ADULT POPULATION</div>
            <div className="text-3xl font-bold text-gray-900">{(data as any).metrics.projectedAdultPopulation}</div>
          </div>
        ) : null}
        </div>
      </div>

      {/* Charts Grid; each card is its own white box with spacing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {renderGenderChart()}
        {renderAgeChart()}
        {renderEducationCard()}
        {renderEmploymentCard()}
        {renderIncomeChart()}
        {renderMaritalChart()}
        {renderChildrenChart()}
      </div>
    </div>
  );
}

// touch
